export interface SelectionGroupItem {
    item_id: number;
    item_name: string;
    item_description?: string;
    price_modifier: number;
    delivery_price_modifier?: number;
    online_price_modifier?: number;
    desk_price_modifier?: number;
    min_selections: number;
    max_selections: number;
  }
  
  export interface SelectionGroup {
    selection_group_id: number;
    selection_group_name: string;
    selection_group_description: string;
    is_price_modifier: boolean;
    min_variance: number;
    max_variance: number;
    min_selections: number;
    max_selections: number;
    items: SelectionGroupItem[];
  }
  
  export interface ModifierOption {
    option_id: number;
    option_name: string;
    option_description: string;
    price_modifier: number;
    delivery_price_modifier?: number;
    online_price_modifier?: number;
    desk_price_modifier?: number;
  }
  
  export interface Modifier {
    modifier_id: number;
    modifier_name: string;
    modifier_description: string;
    is_optional: boolean;
    is_price_setter: boolean;
    options: ModifierOption[];
  }
  
  export interface Item {
    item_id: number;
    parent_item_id?: number;
    item_name: string;
    label: string;
    price: number;
    is_delivery?: boolean;
    price_delivery?: number;
    description?: string;
    main_image?: string;
    image_list?: string[];
    isCombo?: boolean;
    image_url?: string;
    state?: boolean;
    gluten?: boolean;
    vegano?: boolean;
    sodio?: boolean;
    lactosa?: boolean;
    azucar?: boolean;
    vegetariano?: boolean;
    alcohol?: boolean;
    picante?: boolean;
    units?: number;
    is_online?: boolean;
    price_online?: number;
    is_desk?: boolean;
    price_desk?: number;
    category_id?: number;
    local_id?: number;
    unidad_medida_id?: number;
    is_granel?: boolean;
    selection_groups: SelectionGroup[];
    modifiers: Modifier[];
  }

  export interface CartItemSelection {
    selection_id: string | undefined;
    item_id: number;
    item_name: string;
    quantity: number;
    base_price: number;
    selection_groups: {
      selection_group_id: number;
      selection_group_name?: string;
      selected_items: {
        item_id: number;
        item_name: string;
        quantity: number;
        price_modifier?: number;
        comment?: string; // Optional comment for individual selection group items
        course_tag?: 'ENTRADA' | 'FONDO'; // Course tag for individual selection group items
      }[];
    }[];
    modifiers: {
      modifier_id: number;
      option_id: number;
      option_name: string; // Added
    }[];
    total_price: number;
    comment?: string;
    clientIndex: number;
    course_tag?: 'ENTRADA' | 'FONDO'; // Course tagging for double-click functionality
    // Granel-specific fields
    is_granel?: boolean;
    unidad_medida_id?: number;
    unit_acronym?: string;
  }

  export const pedidoTypeMap: Record<string, number> = {
    Delivery: 3,
    Caja: 4, // Caja uses price_desk
    Restaurant: 1, // Restaurant uses base price
    Autoservicio: 2, // Autoservicio uses price_online
  };

  /**
   * Get the correct price for an item based on the pedido type.
   * 
   * @param item - The menu item
   * @param pedidoType - The type of order (Restaurant, Autoservicio, Delivery, Caja)
   * @returns The appropriate price for the given pedido type
   * 
   * Price mapping:
   * - Restaurant (mesas) → base price
   * - Caja & Mesa Virtual → price_desk
   * - Autoservicio → price_online
   * - Delivery/Pickup → price_delivery
   */
  export const getItemPrice = (item: Item, pedidoType: string): number => {
    switch (pedidoType) {
      case 'Restaurant': // Restaurant mesas - uses base price
        return item.price;
      case 'Caja': // Caja & Mesa Virtual - uses price_desk
        return item.price_desk ?? item.price;
      case 'Autoservicio': // Autoservicio - uses price_online
        return item.price_online ?? item.price;
      case 'Delivery': // Delivery/Pickup - uses price_delivery
        return item.price_delivery ?? item.price;
      default:
        return item.price;
    }
  };

  /**
   * Check if an item is available for a specific pedido type.
   * 
   * @param item - The menu item
   * @param pedidoType - The type of order (Restaurant, Autoservicio, Delivery, Caja)
   * @returns true if the item is available for the given pedido type
   * 
   * Availability mapping:
   * - Restaurant (mesas) → checks item.state
   * - Caja & Mesa Virtual → checks item.is_desk
   * - Autoservicio → checks item.is_online
   * - Delivery/Pickup → checks item.is_delivery
   */
  export const isItemAvailable = (item: Item, pedidoType: string): boolean => {
    switch (pedidoType) {
      case 'Restaurant': // Restaurant mesas - checks state
        return item.state ?? true;
      case 'Caja': // Caja & Mesa Virtual - checks is_desk
        return item.is_desk ?? true;
      case 'Autoservicio': // Autoservicio - checks is_online
        return item.is_online ?? true;
      case 'Delivery': // Delivery/Pickup - checks is_delivery
        return item.is_delivery ?? true;
      default:
        return true;
    }
  };

  /**
   * Get the correct price modifier for a selection group item based on the pedido type.
   * 
   * @param selectionItem - The selection group item
   * @param pedidoType - The type of order (Restaurant, Autoservicio, Delivery, Caja)
   * @returns The appropriate price modifier for the given pedido type
   * 
   * Price modifier mapping:
   * - Restaurant (mesas) → base price_modifier
   * - Caja & Mesa Virtual → desk_price_modifier
   * - Autoservicio → online_price_modifier
   * - Delivery/Pickup → delivery_price_modifier
   */
  export const getSelectionItemPriceModifier = (
    selectionItem: SelectionGroupItem, 
    pedidoType: string
  ): number => {
    switch (pedidoType) {
      case 'Restaurant': // Restaurant mesas - uses base price_modifier
        return selectionItem.price_modifier;
      case 'Caja': // Caja & Mesa Virtual - uses desk_price_modifier
        return selectionItem.desk_price_modifier ?? selectionItem.price_modifier;
      case 'Autoservicio': // Autoservicio - uses online_price_modifier
        return selectionItem.online_price_modifier ?? selectionItem.price_modifier;
      case 'Delivery': // Delivery/Pickup - uses delivery_price_modifier
        return selectionItem.delivery_price_modifier ?? selectionItem.price_modifier;
      default:
        return selectionItem.price_modifier;
    }
  };

  /**
   * Get the correct price modifier for a modifier option based on the pedido type.
   * 
   * @param modifierOption - The modifier option
   * @param pedidoType - The type of order (Restaurant, Autoservicio, Delivery, Caja)
   * @returns The appropriate price modifier for the given pedido type
   * 
   * Price modifier mapping:
   * - Restaurant (mesas) → base price_modifier
   * - Caja & Mesa Virtual → desk_price_modifier
   * - Autoservicio → online_price_modifier
   * - Delivery/Pickup → delivery_price_modifier
   */
  export const getModifierOptionPriceModifier = (
    modifierOption: ModifierOption, 
    pedidoType: string
  ): number => {
    switch (pedidoType) {
      case 'Restaurant': // Restaurant mesas - uses base price_modifier
        return modifierOption.price_modifier;
      case 'Caja': // Caja & Mesa Virtual - uses desk_price_modifier
        return modifierOption.desk_price_modifier ?? modifierOption.price_modifier;
      case 'Autoservicio': // Autoservicio - uses online_price_modifier
        return modifierOption.online_price_modifier ?? modifierOption.price_modifier;
      case 'Delivery': // Delivery/Pickup - uses delivery_price_modifier
        return modifierOption.delivery_price_modifier ?? modifierOption.price_modifier;
      default:
        return modifierOption.price_modifier;
    }
  };
